import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getConversationsFromApi,
  updateConversationNameFromApi,
  uploadFile,
} from "modules/chat/services/chatService";
import { v4 as uuidv4 } from "uuid";
import { RootState, AppDispatch } from "core/store/reduxStore";
import { ConversationType, MessageType } from "../components/chat.type";
import CryptoJS from "crypto-js";
import { Provider } from "../components/chat.type";


interface ConversationsState {
  history: { [key: string]: ConversationType };
  current: string;
  userInput: string | File;
  activeModel: string;
  loadingAiResp: "init" | "idle" | "waiting" | "succeeded" | "failed";
  providers: Provider[]
}

const initialState: ConversationsState = {
  history: {},
  current: "",
  userInput: "",
  activeModel: "GPT4o",
  loadingAiResp: "idle",
  providers: []
};

/**
 * Creates a new conversation with provided messages (1 user & 1 assistant)
 * @param messageState current state of the messages slice
 * @param convoState current state of the conversation slice
 * @param dispatch the dispatch functoin
 */
function createNewConversation(convoState, dispatch, userEmail, userPrompt, accessToken) {
  const conversationId = uuidv4();
  let conversationTitle;

  if (typeof userPrompt === "object") {
    conversationTitle = userPrompt.name;
  } else {
    conversationTitle = userPrompt;
  }
  
  conversationTitle =
    conversationTitle.length > 26 ? conversationTitle.substring(0, 26) : conversationTitle;

  // No conversation is in focus, create a new one for these messages
  // Create the conversation object with the new user/assistant messages
  const now = new Date();
  const isoString = now.toISOString();
  const newConversation = {
    _id: conversationId,
    messages: [],
    user: userEmail,
    model: convoState.activeModel,
    conversationTitle: conversationTitle,
    conversationId: conversationId,
    createdAt: isoString,
    updatedAt: isoString,
    __v: 1,
  } as ConversationType;

  // Merge the existing conversation list with the new object
  dispatch(setConversation({ conversation: newConversation }));
  console.log("current inspect createNewConversation")

  // Set as current so this chat can be in focus
  dispatch(setCurrentConversation(newConversation.conversationId));

  // Make a request to the backend to update the name of the conversation
  updateConversationNameFromApi(accessToken, conversationId, conversationTitle);
}

/**
 * Updates the local list of active messages
 * Pushes the userPrompt to the API backend for a response from Gen AI model
 * Updates the list of active messages with the response message
 * @param dispatch
 * @param convoState
 * @param userPrompt
 */
async function addTextToConversation(
  accessToken,
  dispatch,
  convoState,
  userPrompt,
  conversationExists,
  socket
) {
  console.log("addTextToConversation");
  // Set the ai message to status init
  dispatch(setAiMessageStatus("waiting"));
  const conversationId = convoState.current;

  let newTitle;
  // The backend creates an Id as the name for a new conversation, let's make this more user friendly
  if (conversationExists === false) {
    // New Conversation; set the name to be the first 30 characters
    newTitle =
      userPrompt.length > 30 ? userPrompt.substring(0, 30) : userPrompt;
  } else {
    // Conversation already has messages; do nothing, the name is staying as exists in the backend
    newTitle = convoState.history[conversationId].conversationTitle;
  }

  const userID = uuidv4();
  const assistantID = uuidv4();

  const now = new Date();
  const isoString = now.toISOString();

  const userMsg: MessageType = {
    _id: userID,
    chatid: `${userID}:${assistantID}`,
    role: "user",
    model: convoState.activeModel,
    content: userPrompt,
    createdAt: isoString,
    updatedAt: isoString,
  };

  if (socket) {
    const reqData = { 
      content: userPrompt, 
      model: convoState.activeModel,
      conversationid: conversationId, 
      id: `${userID}:${assistantID}`
    }

    const assistantMsg: MessageType = {
      _id: assistantID,
      chatid: `${userID}:${assistantID}`,
      role: "assistant",
      model: reqData.model,
      content: "",
      createdAt: isoString,
      updatedAt: isoString,
    };
    
    console.log("updateConversationMessages pre")
    dispatch(
      updateConversationMessages({
        newMessages: [userMsg, assistantMsg],
        conversationId: conversationId,
        conversationTitle: newTitle,
      })
    );

    console.log(reqData)

    socket.emit("message", JSON.stringify(reqData));
  }
}


async function calculateHash(buffer) {
  const digest = await crypto.subtle.digest("SHA-512", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function addFileToConversation(
  accessToken,
  dispatch,
  convoState,
  file,
  conversationExists,
  socket
) {
  // Set the ai message to status init
  dispatch(setAiMessageStatus("waiting"));

  // Match chunk size with the Python version (3 MB)
  // const chunkSize = 3 * 1024 * 80;
  // let currentPosition = 0;
  const conversationId = convoState.current;
  const activeModel = convoState.activeModel;

  let newTitle = convoState.history[conversationId].conversationTitle;

  const userID = uuidv4();
  const assistantID = uuidv4();

  const now = new Date();
  const isoString = now.toISOString();

  const userMsg: MessageType = {
    _id: userID,
    chatid: `${userID}:${assistantID}`,
    role: "user",
    model: activeModel,
    content: {
      type: "file",
      name: file.name,
      fileType: file.type,
      url: URL.createObjectURL(file),
    },
    createdAt: isoString,
    updatedAt: isoString,
  };

  const assistantMsg: MessageType = {
    _id: assistantID,
    chatid: `${userID}:${assistantID}`,
    role: "assistant",
    model: activeModel,
    content: "",
    createdAt: isoString,
    updatedAt: isoString,
  };

  dispatch(
    updateConversationMessages({
      newMessages: [userMsg, assistantMsg],
      conversationId: conversationId,
      conversationTitle: newTitle,
    })
  );

  const arrayBuffer = await file.arrayBuffer();
  const hash = await calculateHash(arrayBuffer);
  const chunkSize = 1024 * 1024; // 1 MB
  const totalChunks = Math.ceil(file.size / chunkSize);
  let position = 0;
  const reader = new FileReader();

  // Error handling for FileReader
  reader.onerror = function (errorEvent) {
    if(errorEvent.target !== null) {
      console.error("Error reading file:", errorEvent.target.error);
      reader.abort();
    }
  };

  reader.onload = async function (loadEvent: any) {
    if (loadEvent.target) {
      try {
        const fileData = {
          model: activeModel,
          conversationid: conversationId,
          id: `${userID}:${assistantID}`,
          fileinfo: {
            name: file.name,
            hash: hash,
            type: file.type,
            size: file.size,
            position: position,
            totalchunks: totalChunks,
          },
          content: new Uint8Array(loadEvent.target.result),
        };
        console.log(fileData, "filedata");
  
        // Emit the file chunk using your established socket connection
        socket.emit("file_chunk", fileData);
  
        // Move to the next position
        position += chunkSize;
  
        // If there's more of the file left to read, continue reading
        if (position < file.size) {
          readNextChunk();
        } else {
          console.log(`Sent all the file chunks: position: ${position}`)
        }
      } catch (error) {
        console.error("Error during file upload:", error);
      }
    }
  };

  const readNextChunk = () => {
    if (position >= file.size) {
      console.log("File reading completed.");
      return;
    }
    const nextChunkEnd = Math.min(position + chunkSize, file.size);
    const chunk = file.slice(position, nextChunkEnd);
    reader.readAsArrayBuffer(chunk);
  };

  readNextChunk();
}

/**
 * Pushes the updated conversation to the API backend and then updates the local
 * @param dispatch
 * @param getState
 * @param convoState current state of all conversations
 * @param userPrompt prompt just typed by the user
 */
async function updateConversation(
  accessToken,
  dispatch,
  convoState,
  userPrompt,
  conversationExists,
  socket
) {
  let id = "";
  console.log("updateConversation");
  if (typeof userPrompt === "string") {
    // userPrompt is a string
    // Send the prompt, get the response, and save locally to the list of active messages
    await addTextToConversation(
      accessToken,
      dispatch,
      convoState,
      userPrompt,
      conversationExists,
      socket
    );
  } else if (userPrompt instanceof File) {
    // userPrompt is an object
    // Handle the case when userPrompt is an object
    await addFileToConversation(
      accessToken,
      dispatch,
      convoState,
      userPrompt,
      conversationExists,
      socket
    );
  }
}

/**
 * Orchistrates when to create a conversation with no messages or with messages
 * @param dispatch
 * @param getState
 * @param messageState current state of the active messages
 * @param conversationState current state of all conversations
 * @param userPrompt prompt just typed by the user
 */
async function handleNewConversation(
  accessToken,
  dispatch,
  getState,
  conversationState,
  userPrompt,
  userEmail,
  socket
) {
  console.log("call createNewConversation");
  createNewConversation(conversationState, dispatch, userEmail, userPrompt, accessToken);

  console.log("handleNewConversation");
  if (userPrompt !== "") {
    const updatedConversations = getState().chatConversations;
    await updateConversation(
      accessToken,
      dispatch,
      updatedConversations,
      userPrompt,
      false,
      socket
    );
  }
}

/**
 * Pass through function to compliment 'handleNewConversation'
 * @param dispatch
 * @param getState
 * @param conversationState current state of all conversations
 * @param userPrompt prompt just typed by the user
 */
async function handleExistingConversation(
  accessToken,
  dispatch,
  conversationState,
  userPrompt,
  socket
) {
  console.log("handleExistingConversation");
  await updateConversation(
    accessToken,
    dispatch,
    conversationState,
    userPrompt,
    true,
    socket
  );
}

/**
 * Adds messages to an existing or new conversation
 */
export const addToConversation = createAsyncThunk<
  void,
  { userEmail: string; userPrompt: any; accessToken: string, socket: any },
  { state: RootState; dispatch: AppDispatch }
>(
  "addToConversation",
  async (
    { userEmail, userPrompt, accessToken, socket },
    { getState, dispatch }: { getState: () => RootState; dispatch: AppDispatch }
  ) => {
    const conversationState = getState().chatConversations;
    console.log("addToConversation");
    if (conversationState.current === "") {
      console.log("call handleNewConversation");
      await handleNewConversation(
        accessToken,
        dispatch,
        getState,
        conversationState,
        userPrompt,
        userEmail,
        socket
      );
    } else {
      console.log("call handleExistingConversation");
      await handleExistingConversation(
        accessToken,
        dispatch,
        conversationState,
        userPrompt,
        socket
      );
    }
  }
);

/**
 * Adds messages to an existing or new conversation
 */
export const refreshConversations = createAsyncThunk<
  void,
  { accessToken: string },
  { state: RootState; dispatch: AppDispatch }
>(
  "refreshConversations",
  async (
    { accessToken },
    { getState, dispatch }: { getState: () => RootState; dispatch: AppDispatch }
  ) => {
    getConversationsFromApi(accessToken).then((list) => {
      // Ignore the following if no conversations were found
      if (list.length > 0) {
        dispatch(
          // Set the conversations to what we just got from the API
          setConversations({
            // Pass in a k/v map: { [key: convoId]: ConversationType }
            conversations: Object.fromEntries(
              list.map((conversation) => [
                conversation.conversationId,
                conversation,
              ])
            ),
          })
        );
      }
    });
  }
);

/**
 * The Conversation Redux Slice
 */
export const conversationHistorySlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    setProviders: (state, action: PayloadAction<{ providers: any}>) => {
      const { providers } = action.payload;
      state.providers = providers;
    },
    setConversation: (
      state,
      action: PayloadAction<{ conversation: ConversationType }>
    ) => {
      const { conversation } = action.payload;

      // Add the conversation to the dictionary of conversations
      state.history = {
        ...state.history,
        [conversation.conversationId]: conversation,
      };
    },
    setConversations: (state, action: PayloadAction<{ conversations }>) => {
      const { conversations } = action.payload;

      // Add the conversation to the dictionary of conversations
      state.history = conversations;
    },
    removeConversation: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload;
      
      // If current clear this ID from state.current
      if (state.current === conversationId) {
        state.current = "";
      }
    
      // Create a new history object, excluding the conversation with the specified ID
      const newHistory = Object.fromEntries(
        Object.entries(state.history).filter(([key]) => key !== conversationId)
      );
    
      // Update the state with the new history object
      state.history = newHistory;
    },
    setCurrentConversationName: (
      state,
      action: PayloadAction<{
        conversationId: string;
        conversationTitle: string;
      }>
    ) => {
      const { conversationId, conversationTitle } = action.payload;

      state.history = {
        // Merge the existing with our target
        ...state.history,
        [conversationId]: {
          // Add the existing key/val pairs
          ...state.history[conversationId],
          // Update the title
          conversationTitle: conversationTitle,
        },
      };
    },
    updateConversationMessages: (
      state,
      action: PayloadAction<{
        newMessages: MessageType[];
        conversationId: string | undefined;
        conversationTitle?: string;
      }>
    ) => {
      const { newMessages, conversationId, conversationTitle } = action.payload;

      if (conversationId) {
        // Update the state for the Conversations Component
        if (state.history[conversationId]) {
          const updatedConversation = {
            ...state.history[conversationId],
            conversationTitle:
              conversationTitle !== undefined
                ? conversationTitle
                : state.history[conversationId].conversationTitle,

            messages: [
              ...state.history[conversationId].messages,
              ...newMessages,
            ],
          };
          state.history[conversationId] = updatedConversation;
        } else {
          const newconversation = {
            conversationId: conversationId,
            conversationTitle: conversationTitle || "",
            messages: newMessages,
            user: ""
          };
          state.history[conversationId] = newconversation;
        }
      }
    },
    updateMessage: (
      state,
      action: PayloadAction<{
        newMessage: MessageType;
        conversationId: string | undefined;
        isNew: boolean;
      }>
    ) => {
      const { newMessage, conversationId, isNew } = action.payload;
      if (conversationId) {
        // Conversation exists
        if (state.history[conversationId]) {
          const messages = state.history[conversationId].messages;
    
          // Find the message with the matching _id
          const messageIndex = messages.findIndex(msg => msg._id === newMessage._id);
    
          // Handle content for text and file types separately
          let contentString = "";
    
          if (typeof newMessage.content === "string") {
            contentString = newMessage.content;  // If content is a string
          } else if ("text" in newMessage.content) {
            contentString = newMessage.content.text;  // If content has a `text` property
          } else if ("type" in newMessage.content && newMessage.content.type === "file") {
            const file = newMessage.content;
            contentString = `${file.url}`;;
          }
    
          // Update the message at the found index
          messages[messageIndex] = {
            ...messages[messageIndex],
            content: (typeof messages[messageIndex].content === "string"
              ? messages[messageIndex].content
              : "") + contentString,
          };
    
          // Set the updated conversation with the updated message
          state.history[conversationId] = {
            ...state.history[conversationId],
            messages,
          };
        }
      }
    },
    
    setCurrentConversation: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
      state.loadingAiResp = "init";
    },
    clearConversation: (state) => {
      state.current = "";
    },
    setUserInput: (state, action: PayloadAction<string | File>) => {
      state.userInput = typeof action.payload === "string" ? action.payload : "";
    },    
    setActiveModel: (state, action: PayloadAction<string>) => {
      state.activeModel = action.payload;
    },
    setAiMessageStatus: (
      state,
      action: PayloadAction<
        "init" | "idle" | "waiting" | "succeeded" | "failed"
      >
    ) => {
      state.loadingAiResp = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addToConversation.fulfilled, (state, action) => {});
    builder.addCase(refreshConversations.fulfilled, (state, action) => {});
  },
});

export const {
  setProviders,
  setConversation,
  setConversations,
  removeConversation,
  setCurrentConversation,
  clearConversation,
  updateConversationMessages,
  updateMessage,
  setCurrentConversationName,
  setUserInput,
  setActiveModel,
  setAiMessageStatus,
} = conversationHistorySlice.actions;

export default conversationHistorySlice.reducer;
